import { useState } from 'react';
import { invoicePayPath, payAmountProblem, paySurchargeLabel, payTotals, Size, t, type CardType, type StringKey, type PayCheckoutAnswer, type PayOnlineOffer, type PayRail } from '@bms2/shared';
import { NumericInput } from '../../components/controls';
import { RadioGroup } from '../../components/controls/Radio';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Money } from '../../components/Money';
import { Modal } from '../../components/modals/Modal';
import { INVOICES_PATH, post } from './api';
import styles from './PayInvoiceModal.module.css';

const RAIL_LABEL: Record<PayRail, StringKey> = { Card: 'invoice.pay.card', Bank: 'invoice.pay.bank' };
const CARD_TYPES: readonly { value: CardType; label: StringKey }[] = [
  { value: 'Credit', label: 'invoice.pay.credit' },
  { value: 'Debit', label: 'invoice.pay.debit' },
  { value: 'Prepaid', label: 'invoice.pay.prepaid' },
];

/**
 * 🔒 Pay this invoice's window (piece 21; decisions 0239, 0372, 1650): the
 * amount, prefilled with the open balance, Card or Bank transfer, the card's
 * kind before anything else on the Card rail, and the surcharge line and the
 * total beneath, worked out by the same rule the server opens the Checkout
 * with. Continue to payment sends the browser to the processor's own page.
 *
 * 🔨 No card number and no bank detail is asked for here; the processor's
 * page takes them, which is the whole point of the hosted Checkout.
 */
export function PayInvoiceModal({ invoiceId, offer, onCancel, apiBaseUrl = '', navigateTo = (url: string) => window.location.assign(url) }: {
  invoiceId: string;
  offer: PayOnlineOffer;
  onCancel: () => void;
  apiBaseUrl?: string;
  /** Where the browser goes once the Checkout is open; a test reads the address instead of leaving the page. */
  navigateTo?: (url: string) => void;
}) {
  const [amountCents, setAmountCents] = useState<number | null>(offer.openBalanceCents);
  const [rail, setRail] = useState<PayRail | null>(offer.rails.length === 1 ? offer.rails[0]! : null);
  const [cardType, setCardType] = useState<CardType | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const amountProblem = amountCents === null ? null : payAmountProblem(amountCents, offer.openBalanceCents);
  const chosen = amountCents !== null && rail !== null && (rail === 'Bank' || cardType !== null);
  const totals = chosen ? payTotals({ amountCents: amountCents!, rail: rail!, cardType }, offer.creditSurchargePercent) : null;
  const figure = (cents: number) => <Money cents={cents} currencyCode={offer.currencyCode} />;

  const proceed = async () => {
    if (!chosen || amountProblem) return;
    setBusy(true);
    setRefusal(null);
    const answer = await post<PayCheckoutAnswer>(invoicePayPath(INVOICES_PATH, invoiceId), { amountCents, rail, ...(rail === 'Card' ? { cardType } : {}) }, apiBaseUrl);
    if (!answer.ok) {
      setBusy(false);
      setRefusal(answer.message);
      return;
    }
    navigateTo(answer.data.url);
  };

  return (
    <Modal
      open
      title={t('invoice.pay.title')}
      onCancel={onCancel}
      dataModal="invoice-pay"
      actions={[
        { kind: 'primary', label: busy ? t('invoice.pay.opening') : t('invoice.pay.continue'), onAct: () => void proceed(), disabled: busy || !chosen || amountProblem !== null },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      <div className={styles.window} data-invoice-pay="true">
        {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
        <NumericInput size={Size.Medium} label={t('invoice.pay.amount')} kind="currency" value={amountCents} onChange={setAmountCents} name="payAmount" required hint={t('invoice.pay.amountHint')} error={amountProblem} />
        <RadioGroup
          name="payRail"
          legend={t('invoice.pay.how')}
          orientation="horizontal"
          options={offer.rails.map((value) => ({ value, label: t(RAIL_LABEL[value]) }))}
          value={rail}
          onChange={(value) => setRail(value as PayRail)}
        />
        {/* 🔒 The card's kind is asked before anything else on the Card rail, and the surcharge line follows from it (decision 0372). */}
        {rail === 'Card' ? (
          <RadioGroup name="payCardType" legend={t('invoice.pay.cardType')} orientation="horizontal" options={CARD_TYPES.map((row) => ({ value: row.value, label: t(row.label) }))} value={cardType} onChange={(value) => setCardType(value as CardType)} />
        ) : null}
        {totals ? (
          <dl className={styles.totals} data-invoice-pay-totals={totals.totalCents}>
            <dt>{t('invoice.pay.amount')}</dt>
            <dd>{figure(amountCents!)}</dd>
            {/* 🔒 The surcharge sits beneath the amount where tax sits on an invoice, and only where the card's kind carries one (decision 0239). */}
            {totals.surchargeCents > 0 ? (
              <>
                <dt data-invoice-pay-surcharge={totals.surchargeCents}>{paySurchargeLabel(offer.creditSurchargePercent)}</dt>
                <dd>{figure(totals.surchargeCents)}</dd>
              </>
            ) : null}
            <dt className={styles.total}>{t('invoice.pay.total')}</dt>
            <dd className={styles.total}>{figure(totals.totalCents)}</dd>
          </dl>
        ) : null}
        <p className={styles.note}>{t('invoice.pay.secure')}</p>
      </div>
    </Modal>
  );
}
